import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, FC, MouseEvent } from 'react';

import InfoCard from 'components/InfoCard/InfoCard';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import type { RoofTopI } from 'declarations/dto/roofTops';
import { StorageE } from 'declarations/enums/storage';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import type { SubFilterI } from 'declarations/interfaces/common';
import type { TicketCountByRoofTopI } from 'declarations/interfaces/tickets';
import CardLoader from 'pages/TicketsPage/components/CardLoader/CardLoader';
import { CardLoaderWrapStyled } from 'pages/TicketsPage/components/CardLoader/CardLoaderWrapStyled';
import { ReportCardWrapStyled } from 'pages/TicketsPage/TicketsPage.styled';

import { DealerTableWrapStyled, SortIconWrapStyled } from './DealerCard.styled';

type SortColumnT = 'dealer' | 'amount' | 'warrcloud' | 'dealerCount' | 'oem';
type SortOrderT = 'asc' | 'desc';

const DealerCard: FC = (): JSX.Element => {
  const {
    getSearchParam,
    ticketsCountByRoofTop,
    activeTab,
    updateSearchParamsArray,
    isLoadingTicketsCountByRoofTop,
    roofTopsList,
    dealerSelectedRooftopIds,
    setDealerSelectedRooftopIds,
    oemFilter,
    setOemFilter,
    dealerGroupFilter,
    setDealerGroupFilter,
  } = useTickets();
  const { userProfile } = useUserProfile();
  const { isMobile, isSmallMobile } = useResponsive();

  const [sortColumn, setSortColumn] = useState<SortColumnT>('dealer');
  const [sortOrder, setSortOrder] = useState<SortOrderT>('asc');
  const [oemMenuAnchor, setOemMenuAnchor] = useState<HTMLElement | null>(null);
  const [dealerGroupMenuAnchor, setDealerGroupMenuAnchor] = useState<HTMLElement | null>(null);
  const [oemSearchQuery, setOemSearchQuery] = useState('');
  const [dealerGroupSearchQuery, setDealerGroupSearchQuery] = useState('');
  const [isDealerCardExpanded, setIsDealerCardExpanded] = useState((): boolean => {
    try {
      const stored = localStorage.getItem(StorageE.DealerCardExpanded);
      return stored === 'true';
    } catch {
      return false;
    }
  });

  useEffect((): void => {
    try {
      localStorage.setItem(StorageE.DealerCardExpanded, String(isDealerCardExpanded));
    } catch {
      // Ignore storage errors
    }
  }, [isDealerCardExpanded]);

  const dataKey = activeTab === ActiveTabTypeE.Schedule ? 'schedules' : 'tickets';
  const countData = ticketsCountByRoofTop?.[dataKey];

  /** Applied selection (from context). */
  const dealerRefineSelectedIds = useMemo((): string[] => {
    if (!dealerSelectedRooftopIds || dealerSelectedRooftopIds.trim() === '') {
      return [];
    }
    return dealerSelectedRooftopIds
      .split(',')
      .map((id): string => id.trim())
      .filter(Boolean);
  }, [dealerSelectedRooftopIds]);

  const [dealerRefineMenuAnchor, setDealerRefineMenuAnchor] = useState<HTMLElement | null>(null);
  /** Pending selection (only applied when user clicks Apply). */
  const [pendingDealerIds, setPendingDealerIds] = useState<string[] | null>(null);
  const [dealerRefineSearchQuery, setDealerRefineSearchQuery] = useState('');

  const dealerRefineMenuOpen = Boolean(dealerRefineMenuAnchor);

  const handleDealerRefineMenuOpen = (event: MouseEvent<HTMLButtonElement>): void => {
    setPendingDealerIds(dealerRefineSelectedIds.length === 0 ? null : [...dealerRefineSelectedIds]);
    setDealerRefineSearchQuery('');
    setDealerRefineMenuAnchor(event.currentTarget);
  };

  const handleDealerRefineMenuClose = (): void => {
    setDealerRefineMenuAnchor(null);
    setDealerRefineSearchQuery('');
  };

  /** Dealers in refinement list filtered by search, OEM, and dealer group (for menu only).
   * When no OEM or dealer groups are selected, show all dealer options (roofTopsList, or userProfile.rooftopPermissions as fallback). */
  const dealerRefineFilteredList = useMemo((): RoofTopI[] => {
    const activeOemIds = oemFilter.filter(o => o.checked).map(o => o.value);
    const activeDealerGroupIds = dealerGroupFilter.filter(g => g.checked).map(g => g.value);

    let list: RoofTopI[];
    if (activeOemIds.length === 0 && activeDealerGroupIds.length === 0) {
      const roofTops = roofTopsList ?? [];
      const allDealerOptions =
        roofTops.length > 0
          ? roofTops
          : userProfile?.rooftopPermissions?.map(
              (r): RoofTopI => ({
                id: r.rooftopId,
                name: r.rooftop,
                accountId: '',
                oemFamilyId: '',
              })
            ) ?? [];
      list = allDealerOptions;
    } else {
      list = roofTopsList ?? [];
      if (activeDealerGroupIds.length > 0) {
        list = list.filter(r => activeDealerGroupIds.includes(r.accountId));
      }
      if (activeOemIds.length > 0) {
        list = list.filter(r => activeOemIds.includes(r.oemFamilyId));
      }
    }

    const q = dealerRefineSearchQuery.trim().toLowerCase();
    if (q) list = list.filter((r): boolean => (r.name ?? '').toLowerCase().includes(q));
    return list;
  }, [
    roofTopsList,
    userProfile?.rooftopPermissions,
    dealerRefineSearchQuery,
    oemFilter,
    dealerGroupFilter,
  ]);

  const oemFilterCount = oemFilter.filter(f => f.checked).length;
  const dealerGroupFilterCount = dealerGroupFilter.filter(f => f.checked).length;

  /** Dealer groups displayed in dropdown: when OEM filter is active, only show groups that have rooftops for those OEMs. */
  const dealerGroupFilterDisplayed = useMemo((): SubFilterI[] => {
    const activeOemIds = oemFilter.filter(o => o.checked).map(o => o.value);
    if (activeOemIds.length === 0) return dealerGroupFilter;
    const accountIdsWithSelectedOems = new Set(
      (roofTopsList ?? []).filter(r => activeOemIds.includes(r.oemFamilyId)).map(r => r.accountId)
    );
    return dealerGroupFilter.filter(g => accountIdsWithSelectedOems.has(g.value));
  }, [dealerGroupFilter, oemFilter, roofTopsList]);

  const handleOemToggle = (value: string): void => {
    setOemMenuAnchor(null);
    setOemSearchQuery('');
    setOemFilter(prev => prev.map(o => (o.value === value ? { ...o, checked: !o.checked } : o)));
    setDealerGroupFilter(prev => prev.map(g => ({ ...g, checked: false })));
    setDealerSelectedRooftopIds(null);
  };

  const handleDealerGroupToggle = (value: string): void => {
    setDealerGroupFilter(prev =>
      prev.map(g => (g.value === value ? { ...g, checked: !g.checked } : g))
    );
    setDealerSelectedRooftopIds(null);
  };

  const handlePendingToggleAll = (): void => {
    const visibleIds = dealerRefineFilteredList.map((r): string => r.id);
    const pendingIds = pendingDealerIds;
    const allSelected =
      visibleIds.length > 0 &&
      pendingIds !== null &&
      visibleIds.every((id): boolean => pendingIds.includes(id));
    if (allSelected) {
      setPendingDealerIds(null);
    } else {
      setPendingDealerIds(visibleIds.length > 0 ? visibleIds : null);
    }
  };

  const handlePendingToggleDealer = (roofId: string): void => {
    setPendingDealerIds((prev): string[] | null => {
      if (prev === null) return [roofId];
      const next = prev.includes(roofId)
        ? prev.filter((id): boolean => id !== roofId)
        : [...prev, roofId];
      return next.length === 0 ? null : next;
    });
  };

  const handleDealerRefineApply = (): void => {
    setDealerSelectedRooftopIds(pendingDealerIds === null ? null : pendingDealerIds.join(','));
    setDealerRefineMenuAnchor(null);
  };

  const handleDealerRefineReset = (): void => {
    setOemFilter(prev => prev.map(o => ({ ...o, checked: false })));
    setDealerGroupFilter(prev => prev.map(g => ({ ...g, checked: false })));
    setDealerSelectedRooftopIds(null);
    setPendingDealerIds(null);
    setDealerRefineMenuAnchor(null);
  };

  const dealerRefineCount = dealerRefineSelectedIds.length;

  /** True when all dealers currently shown in the dropdown are selected. */
  const isAllDealersInListSelected = useMemo((): boolean => {
    const visibleIds = dealerRefineFilteredList.map((r): string => r.id);
    const pendingIds = pendingDealerIds;
    if (visibleIds.length === 0) return pendingIds === null;
    return pendingIds !== null && visibleIds.every((id): boolean => pendingIds.includes(id));
  }, [dealerRefineFilteredList, pendingDealerIds]);

  // Get single selected dealer ID from URL (mutually exclusive)
  const selectedDealerId = useMemo((): string | null => {
    const raw = getSearchParam(TicketsRefinementKeysE.RoofTopId) || '';
    return raw || null;
  }, [getSearchParam]);

  // Dealers shown in card; only filtered when user has clicked Apply in dealer dropdown
  const allDealers = useMemo((): TicketCountByRoofTopI[] => {
    const raw = countData?.countByRoofTop || [];
    if (dealerRefineSelectedIds.length === 0) return raw;
    const idSet = new Set(dealerRefineSelectedIds);
    return raw.filter((d): boolean => idSet.has(d.roofTopId));
  }, [countData?.countByRoofTop, dealerRefineSelectedIds]);

  // Sort dealers (selected stays in place; no bubbling to top)
  const sortedDealers = useMemo((): TicketCountByRoofTopI[] => {
    const dealers = [...allDealers];
    dealers.sort((a, b) => {
      let comparison = 0;
      switch (sortColumn) {
        case 'dealer':
          comparison = a.roofTopName.localeCompare(b.roofTopName);
          break;
        case 'amount':
          comparison = a.amount - b.amount;
          break;
        case 'warrcloud':
          comparison = a.assignedToWCCount - b.assignedToWCCount;
          break;
        case 'dealerCount':
          comparison = a.assignedToDealerCount - b.assignedToDealerCount;
          break;
        case 'oem':
          comparison = a.assignedToOEMCount - b.assignedToOEMCount;
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return dealers;
  }, [allDealers, sortColumn, sortOrder]);

  // Calculate totals for the "All" row (filtered by dealer refinement when Apply is clicked)
  const totals = useMemo(() => {
    return {
      amount: allDealers.reduce((sum, d) => sum + d.amount, 0),
      warrcloud: allDealers.reduce((sum, d) => sum + d.assignedToWCCount, 0),
      dealer: allDealers.reduce((sum, d) => sum + d.assignedToDealerCount, 0),
      oem: allDealers.reduce((sum, d) => sum + d.assignedToOEMCount, 0),
    };
  }, [allDealers]);

  const handleSort = (column: SortColumnT) => (): void => {
    if (sortColumn === column) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  /** Select dealer (same as dropdown): null = All, or roofTopId; click selected again = clear to All */
  const handleSelectDealer = (dealerId: string | null) => (): void => {
    if (dealerId === null) {
      updateSearchParamsArray(TicketsRefinementKeysE.RoofTopId, null);
    } else if (selectedDealerId === dealerId) {
      updateSearchParamsArray(TicketsRefinementKeysE.RoofTopId, null);
    } else {
      updateSearchParamsArray(TicketsRefinementKeysE.RoofTopId, [dealerId]);
    }
  };

  const isSchedule = activeTab === ActiveTabTypeE.Schedule;
  const rowClickSx = {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: (theme: Theme): string => theme.palette.action?.hover ?? 'rgba(0,0,0,0.04)',
    },
  };

  const renderSortIcon = (column: SortColumnT): JSX.Element | null => {
    if (sortColumn !== column) return null;
    return (
      <SortIconWrapStyled>
        {sortOrder === 'asc' ? (
          <ArrowUpwardIcon sx={{ fontSize: 14 }} />
        ) : (
          <ArrowDownwardIcon sx={{ fontSize: 14 }} />
        )}
      </SortIconWrapStyled>
    );
  };

  const formatAmount = (value: number): string => {
    return `$${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Color logic for counts: green=0, orange=1-10, red=11+
  const getCountColor = (count: number): string => {
    if (count === 0) return '#4CAF50'; // green
    if (count <= 10) return '#FF9800'; // orange
    return '#F44336'; // red
  };

  // Check if dealer needs schedule upload (highlight in Schedule tab)
  const shouldHighlightScheduleUpload = (dealer: TicketCountByRoofTopI): boolean => {
    return dealer.isLastScheduleUploadedMoreThanAWeek && activeTab === ActiveTabTypeE.Schedule;
  };

  const handleDealerExpandChange = (_e: React.SyntheticEvent, expanded: boolean): void => {
    setIsDealerCardExpanded(expanded);
  };

  const selectedDealerRowRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    if (selectedDealerId) {
      if (isMobile && !isDealerCardExpanded) {
        setIsDealerCardExpanded(true);
      }
      const row = selectedDealerRowRef.current;
      if (row) {
        requestAnimationFrame(() => {
          row.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }
  }, [selectedDealerId, isMobile, isDealerCardExpanded, isLoadingTicketsCountByRoofTop]);

  if (isMobile) {
    return (
      <ReportCardWrapStyled>
        <Accordion
          expanded={isDealerCardExpanded}
          onChange={handleDealerExpandChange}
          disableGutters
          sx={{
            boxShadow: 'none',
            '&:before': { display: 'none' },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            '&.MuiAccordion-root': {
              backgroundColor: (theme): string => theme.palette.background.paper,
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
            sx={{
              minHeight: 46,
              '& .MuiAccordionSummary-content': { margin: 0, alignItems: 'center' },
              '& .MuiAccordionSummary-expandIconWrapper': {
                transform: 'none',
                '&.Mui-expanded': { transform: 'rotate(180deg)' },
              },
            }}
          >
            <Typography sx={{ fontSize: 15, fontWeight: 500 }}>Dealers</Typography>
            {!isSmallMobile && (
              <Typography sx={{ color: 'success.main', fontSize: 15, fontWeight: 500, ml: 1 }}>
                ({allDealers.length})
              </Typography>
            )}
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1, px: 0 }}>
            <Box
              sx={{ maxHeight: '50vh', overflow: 'auto', display: 'flex', flexDirection: 'column' }}
            >
              <InfoCard fullWidth={false} compact>
                {isLoadingTicketsCountByRoofTop ? (
                  <CardLoaderWrapStyled>
                    <CardLoader />
                  </CardLoaderWrapStyled>
                ) : (
                  <>
                    <DealerTableWrapStyled>
                      <Table size="small" aria-label="Dealers table">
                        {!isSmallMobile && (
                          <TableHead>
                            <TableRow
                              sx={{
                                backgroundColor: 'background.paper',
                                '& th': {
                                  position: 'sticky',
                                  top: 0,
                                  backgroundColor: 'background.paper',
                                  zIndex: 1,
                                },
                              }}
                            >
                              <TableCell sx={{ fontWeight: 700 }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    flexWrap: 'nowrap',
                                    minWidth: 0,
                                    '& > *': { flexShrink: 0 },
                                  }}
                                >
                                  <Box
                                    component="span"
                                    onClick={handleSort('dealer')}
                                    sx={{
                                      cursor: 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      fontWeight: 700,
                                    }}
                                  >
                                    Dealers{renderSortIcon('dealer')}
                                  </Box>
                                  {!isSmallMobile && (
                                    <>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                          minWidth: 90,
                                          justifyContent: 'space-between',
                                          fontSize: 12,
                                          height: 28,
                                          textTransform: 'none',
                                        }}
                                        endIcon={<ArrowDownwardIcon sx={{ fontSize: 14 }} />}
                                        onClick={(e): void => {
                                          setOemSearchQuery('');
                                          setOemMenuAnchor(e.currentTarget);
                                        }}
                                      >
                                        OEMs
                                        {oemFilterCount > 0 ? (
                                          <>
                                            {' '}
                                            <Box
                                              component="span"
                                              sx={{ color: 'success.main', fontWeight: 500 }}
                                            >
                                              {`(${oemFilterCount})`}
                                            </Box>
                                          </>
                                        ) : null}
                                      </Button>
                                      <Menu
                                        anchorEl={oemMenuAnchor}
                                        open={Boolean(oemMenuAnchor)}
                                        onClose={(): void => {
                                          setOemMenuAnchor(null);
                                          setOemSearchQuery('');
                                        }}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                        PaperProps={{
                                          sx: {
                                            maxHeight: 380,
                                            minWidth: 200,
                                            width: 'max-content',
                                            maxWidth: 500,
                                            display: 'flex',
                                            flexDirection: 'column',
                                          },
                                        }}
                                        MenuListProps={{
                                          sx: {
                                            display: 'flex',
                                            flexDirection: 'column',
                                            flex: 1,
                                            minHeight: 0,
                                            p: 0,
                                            overflow: 'hidden',
                                          },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            flexShrink: 0,
                                            p: 1,
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                          }}
                                        >
                                          <TextField
                                            size="small"
                                            placeholder="Search OEMs"
                                            value={oemSearchQuery}
                                            onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                                              setOemSearchQuery(e.target.value)
                                            }
                                            variant="outlined"
                                            fullWidth
                                            InputProps={{
                                              startAdornment: (
                                                <InputAdornment position="start">
                                                  <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                              ),
                                              endAdornment: oemSearchQuery ? (
                                                <InputAdornment position="end">
                                                  <IconButton
                                                    aria-label="Clear OEM search"
                                                    size="small"
                                                    onClick={(): void => setOemSearchQuery('')}
                                                  >
                                                    <ClearIcon fontSize="small" />
                                                  </IconButton>
                                                </InputAdornment>
                                              ) : undefined,
                                            }}
                                          />
                                        </Box>
                                        <List
                                          dense
                                          sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
                                        >
                                          {oemFilter
                                            .filter(
                                              (oem): boolean =>
                                                !oemSearchQuery.trim() ||
                                                oem.label
                                                  .toLowerCase()
                                                  .includes(oemSearchQuery.trim().toLowerCase())
                                            )
                                            .map(
                                              (oem): JSX.Element => (
                                                <MenuItem
                                                  key={oem.value}
                                                  onClick={(): void => handleOemToggle(oem.value)}
                                                >
                                                  <Checkbox
                                                    checked={oem.checked}
                                                    disableRipple
                                                    size="small"
                                                  />
                                                  <ListItemText
                                                    primary={oem.label}
                                                    sx={{ '& span': { whiteSpace: 'nowrap' } }}
                                                  />
                                                </MenuItem>
                                              )
                                            )}
                                          {oemSearchQuery.trim() &&
                                            oemFilter.filter((oem): boolean =>
                                              oem.label
                                                .toLowerCase()
                                                .includes(oemSearchQuery.trim().toLowerCase())
                                            ).length === 0 && (
                                              <Typography
                                                sx={{
                                                  fontSize: 12,
                                                  color: 'text.secondary',
                                                  px: 2,
                                                  py: 1,
                                                }}
                                              >
                                                No OEMs match your search.
                                              </Typography>
                                            )}
                                        </List>
                                      </Menu>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                          minWidth: 100,
                                          justifyContent: 'space-between',
                                          fontSize: 12,
                                          height: 28,
                                          textTransform: 'none',
                                        }}
                                        endIcon={<ArrowDownwardIcon sx={{ fontSize: 14 }} />}
                                        onClick={(e): void => {
                                          setDealerGroupSearchQuery('');
                                          setDealerGroupMenuAnchor(e.currentTarget);
                                        }}
                                      >
                                        Dealer Groups
                                        {dealerGroupFilterCount > 0 ? (
                                          <>
                                            {'  '}
                                            <Box
                                              component="span"
                                              sx={{ color: 'success.main', fontWeight: 500 }}
                                            >
                                              {`(${dealerGroupFilterCount})`}
                                            </Box>
                                          </>
                                        ) : null}
                                      </Button>
                                      <Menu
                                        anchorEl={dealerGroupMenuAnchor}
                                        open={Boolean(dealerGroupMenuAnchor)}
                                        onClose={(): void => {
                                          setDealerGroupMenuAnchor(null);
                                          setDealerGroupSearchQuery('');
                                        }}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                        PaperProps={{
                                          sx: {
                                            maxHeight: 380,
                                            minWidth: 200,
                                            width: 'max-content',
                                            maxWidth: 500,
                                            display: 'flex',
                                            flexDirection: 'column',
                                          },
                                        }}
                                        MenuListProps={{
                                          sx: {
                                            display: 'flex',
                                            flexDirection: 'column',
                                            flex: 1,
                                            minHeight: 0,
                                            p: 0,
                                            overflow: 'hidden',
                                          },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            flexShrink: 0,
                                            p: 1,
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                          }}
                                        >
                                          <TextField
                                            size="small"
                                            placeholder="Search dealer groups"
                                            value={dealerGroupSearchQuery}
                                            onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                                              setDealerGroupSearchQuery(e.target.value)
                                            }
                                            variant="outlined"
                                            fullWidth
                                            InputProps={{
                                              startAdornment: (
                                                <InputAdornment position="start">
                                                  <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                              ),
                                              endAdornment: dealerGroupSearchQuery ? (
                                                <InputAdornment position="end">
                                                  <IconButton
                                                    aria-label="Clear dealer group search"
                                                    size="small"
                                                    onClick={(): void =>
                                                      setDealerGroupSearchQuery('')
                                                    }
                                                  >
                                                    <ClearIcon fontSize="small" />
                                                  </IconButton>
                                                </InputAdornment>
                                              ) : undefined,
                                            }}
                                          />
                                        </Box>
                                        <List
                                          dense
                                          sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
                                        >
                                          {dealerGroupFilterDisplayed
                                            .filter(
                                              (dg): boolean =>
                                                !dealerGroupSearchQuery.trim() ||
                                                dg.label
                                                  .toLowerCase()
                                                  .includes(
                                                    dealerGroupSearchQuery.trim().toLowerCase()
                                                  )
                                            )
                                            .map(
                                              (dg): JSX.Element => (
                                                <MenuItem
                                                  key={dg.value}
                                                  onClick={(): void =>
                                                    handleDealerGroupToggle(dg.value)
                                                  }
                                                >
                                                  <Checkbox
                                                    checked={dg.checked}
                                                    disableRipple
                                                    size="small"
                                                  />
                                                  <ListItemText
                                                    primary={dg.label}
                                                    sx={{ '& span': { whiteSpace: 'nowrap' } }}
                                                  />
                                                </MenuItem>
                                              )
                                            )}
                                          {dealerGroupSearchQuery.trim() &&
                                            dealerGroupFilterDisplayed.filter((dg): boolean =>
                                              dg.label
                                                .toLowerCase()
                                                .includes(
                                                  dealerGroupSearchQuery.trim().toLowerCase()
                                                )
                                            ).length === 0 && (
                                              <Typography
                                                sx={{
                                                  fontSize: 12,
                                                  color: 'text.secondary',
                                                  px: 2,
                                                  py: 1,
                                                }}
                                              >
                                                No dealer groups match your search.
                                              </Typography>
                                            )}
                                        </List>
                                      </Menu>
                                      <Button
                                        id="dealer-refine-button"
                                        aria-haspopup="listbox"
                                        aria-expanded={dealerRefineMenuOpen}
                                        onClick={handleDealerRefineMenuOpen}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                          minWidth: 120,
                                          justifyContent: 'space-between',
                                          fontSize: 12,
                                          height: 28,
                                          textTransform: 'none',
                                        }}
                                        endIcon={<ArrowDownwardIcon sx={{ fontSize: 14 }} />}
                                      >
                                        Dealers
                                        {dealerRefineCount > 0 ? (
                                          <>
                                            {' '}
                                            <Box
                                              component="span"
                                              sx={{ color: 'success.main', fontWeight: 500 }}
                                            >
                                              {`(${dealerRefineCount})`}
                                            </Box>
                                          </>
                                        ) : null}
                                      </Button>
                                      <Tooltip
                                        title="Select dealers to refine the list"
                                        arrow
                                        placement="top"
                                        slotProps={{
                                          popper: {
                                            modifiers: [{ name: 'flip', enabled: true }],
                                            sx: { zIndex: 1300 },
                                          },
                                        }}
                                      >
                                        <span style={{ display: 'inline-flex', lineHeight: 0 }}>
                                          <InfoOutlinedIcon
                                            sx={{
                                              fontSize: 18,
                                              color: 'action.active',
                                              cursor: 'help',
                                            }}
                                            aria-label="Select dealers to refine the list"
                                          />
                                        </span>
                                      </Tooltip>
                                    </>
                                  )}
                                </Box>
                                {!isSmallMobile && (
                                  <Menu
                                    anchorEl={dealerRefineMenuAnchor}
                                    open={Boolean(dealerRefineMenuAnchor)}
                                    onClose={handleDealerRefineMenuClose}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    PaperProps={{
                                      sx: {
                                        maxHeight: 380,
                                        width: 280,
                                        display: 'flex',
                                        flexDirection: 'column',
                                      },
                                    }}
                                    MenuListProps={{
                                      sx: {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flex: 1,
                                        minHeight: 0,
                                        p: 0,
                                        overflow: 'hidden',
                                      },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        flexShrink: 0,
                                        p: 1,
                                        borderBottom: 1,
                                        borderColor: 'divider',
                                      }}
                                    >
                                      <TextField
                                        size="small"
                                        placeholder="Search dealers"
                                        value={dealerRefineSearchQuery}
                                        onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                                          setDealerRefineSearchQuery(e.target.value)
                                        }
                                        variant="outlined"
                                        fullWidth
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                          ),
                                          endAdornment: dealerRefineSearchQuery ? (
                                            <InputAdornment position="end">
                                              <IconButton
                                                aria-label="Clear dealer search"
                                                size="small"
                                                onClick={(): void => setDealerRefineSearchQuery('')}
                                              >
                                                <ClearIcon fontSize="small" />
                                              </IconButton>
                                            </InputAdornment>
                                          ) : undefined,
                                        }}
                                      />
                                    </Box>
                                    <List dense sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                                      <ListItemButton onClick={handlePendingToggleAll}>
                                        <ListItemIcon>
                                          <Checkbox
                                            checked={isAllDealersInListSelected}
                                            edge="start"
                                            disableRipple
                                          />
                                        </ListItemIcon>
                                        <ListItemText primary="All" />
                                      </ListItemButton>
                                      {dealerRefineFilteredList.map(
                                        (roof): JSX.Element => (
                                          <ListItemButton
                                            key={roof.id}
                                            onClick={(): void => handlePendingToggleDealer(roof.id)}
                                          >
                                            <ListItemIcon>
                                              <Checkbox
                                                checked={
                                                  pendingDealerIds !== null &&
                                                  pendingDealerIds.includes(roof.id)
                                                }
                                                edge="start"
                                                disableRipple
                                              />
                                            </ListItemIcon>
                                            <ListItemText primary={roof.name} />
                                          </ListItemButton>
                                        )
                                      )}
                                      {dealerRefineSearchQuery.trim() &&
                                        dealerRefineFilteredList.length === 0 && (
                                          <Typography
                                            sx={{
                                              fontSize: 12,
                                              color: 'text.secondary',
                                              px: 2,
                                              py: 1,
                                            }}
                                          >
                                            No dealers match your search.
                                          </Typography>
                                        )}
                                    </List>
                                    <Box
                                      sx={{
                                        flexShrink: 0,
                                        p: 1.5,
                                        borderTop: 1,
                                        borderColor: 'divider',
                                        backgroundColor: 'background.paper',
                                        display: 'flex',
                                        gap: 1,
                                      }}
                                    >
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleDealerRefineReset}
                                        sx={{ flex: 1 }}
                                      >
                                        Reset
                                      </Button>
                                      <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleDealerRefineApply}
                                        sx={{ flex: 1 }}
                                      >
                                        Apply
                                      </Button>
                                    </Box>
                                  </Menu>
                                )}
                              </TableCell>
                              {!isSchedule && !isSmallMobile && (
                                <>
                                  <TableCell
                                    sx={{
                                      textAlign: 'right',
                                      whiteSpace: 'nowrap',
                                      fontWeight: 700,
                                    }}
                                    onClick={handleSort('amount')}
                                  >
                                    Amount{renderSortIcon('amount')}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      textAlign: 'right',
                                      whiteSpace: 'nowrap',
                                      fontWeight: 700,
                                    }}
                                    onClick={handleSort('warrcloud')}
                                  >
                                    WarrCloud{renderSortIcon('warrcloud')}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      textAlign: 'right',
                                      whiteSpace: 'nowrap',
                                      fontWeight: 700,
                                    }}
                                    onClick={handleSort('dealerCount')}
                                  >
                                    Dealer{renderSortIcon('dealerCount')}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      textAlign: 'right',
                                      whiteSpace: 'nowrap',
                                      fontWeight: 700,
                                    }}
                                    onClick={handleSort('oem')}
                                  >
                                    OEM{renderSortIcon('oem')}
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          </TableHead>
                        )}
                        <TableBody>
                          {/* All row - click to select All (clear dealer filter); bold when selected */}
                          <TableRow
                            onClick={handleSelectDealer(null)}
                            sx={{
                              ...rowClickSx,
                              backgroundColor: (theme: Theme): string =>
                                !selectedDealerId
                                  ? alpha(theme.palette.primary.main, 0.2)
                                  : 'transparent',
                            }}
                          >
                            <TableCell sx={{ fontWeight: !selectedDealerId ? 700 : 400 }}>
                              All
                            </TableCell>
                            {!isSchedule && !isSmallMobile && (
                              <>
                                <TableCell
                                  sx={{
                                    fontWeight: !selectedDealerId ? 700 : 400,
                                    textAlign: 'right',
                                  }}
                                >
                                  {formatAmount(totals.amount)}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    textAlign: 'right',
                                    color: getCountColor(totals.warrcloud),
                                    fontWeight: !selectedDealerId ? 700 : 600,
                                  }}
                                >
                                  {totals.warrcloud}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    textAlign: 'right',
                                    color: getCountColor(totals.dealer),
                                    fontWeight: !selectedDealerId ? 700 : 600,
                                  }}
                                >
                                  {totals.dealer}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    textAlign: 'right',
                                    color: getCountColor(totals.oem),
                                    fontWeight: !selectedDealerId ? 700 : 600,
                                  }}
                                >
                                  {totals.oem}
                                </TableCell>
                              </>
                            )}
                          </TableRow>

                          {/* Dealer rows - sorted; selected stays in place, highlighted; click to select */}
                          {sortedDealers.map(dealer => {
                            const needsScheduleUpload = shouldHighlightScheduleUpload(dealer);
                            const isSelected = dealer.roofTopId === selectedDealerId;
                            const getRowBg = (theme: Theme): string => {
                              if (needsScheduleUpload)
                                return alpha(theme.palette.warning.main, 0.22);
                              if (isSelected) return alpha(theme.palette.primary.main, 0.2);
                              return 'transparent';
                            };
                            return (
                              <TableRow
                                key={dealer.roofTopId}
                                ref={isSelected ? selectedDealerRowRef : undefined}
                                onClick={handleSelectDealer(dealer.roofTopId)}
                                title={
                                  needsScheduleUpload
                                    ? 'Please upload a Schedule'
                                    : dealer.roofTopName
                                }
                                sx={{
                                  ...rowClickSx,
                                  backgroundColor: getRowBg,
                                  ...(isSelected && { scrollMarginTop: 36 }),
                                }}
                              >
                                <TableCell
                                  sx={{
                                    fontWeight: isSelected ? 700 : 400,
                                    color: (theme: Theme): string =>
                                      needsScheduleUpload ? theme.palette.warning.dark : 'inherit',
                                  }}
                                >
                                  {dealer.roofTopName}
                                </TableCell>
                                {!isSchedule && !isSmallMobile && (
                                  <>
                                    <TableCell
                                      sx={{
                                        fontWeight: isSelected ? 700 : 400,
                                        textAlign: 'right',
                                      }}
                                    >
                                      {formatAmount(dealer.amount)}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        textAlign: 'right',
                                        color: getCountColor(dealer.assignedToWCCount),
                                        fontWeight: isSelected ? 700 : 600,
                                      }}
                                    >
                                      {dealer.assignedToWCCount}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        textAlign: 'right',
                                        color: getCountColor(dealer.assignedToDealerCount),
                                        fontWeight: isSelected ? 700 : 600,
                                      }}
                                    >
                                      {dealer.assignedToDealerCount}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        textAlign: 'right',
                                        color: getCountColor(dealer.assignedToOEMCount),
                                        fontWeight: isSelected ? 700 : 600,
                                      }}
                                    >
                                      {dealer.assignedToOEMCount}
                                    </TableCell>
                                  </>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </DealerTableWrapStyled>
                  </>
                )}
              </InfoCard>
            </Box>
          </AccordionDetails>
        </Accordion>
      </ReportCardWrapStyled>
    );
  }

  return (
    <ReportCardWrapStyled>
      <InfoCard fullWidth={false} compact>
        {isLoadingTicketsCountByRoofTop ? (
          <CardLoaderWrapStyled>
            <CardLoader />
          </CardLoaderWrapStyled>
        ) : (
          <>
            <DealerTableWrapStyled>
              <Table size="small" aria-label="Dealers table">
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: 'background.paper',
                      '& th': {
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'background.paper',
                        zIndex: 1,
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'nowrap',
                          minWidth: 0,
                          '& > *': { flexShrink: 0 },
                        }}
                      >
                        <Box
                          component="span"
                          onClick={handleSort('dealer')}
                          sx={{
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            fontWeight: 700,
                          }}
                        >
                          Dealers{renderSortIcon('dealer')}
                        </Box>
                        <>
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{
                              minWidth: 90,
                              justifyContent: 'space-between',
                              fontSize: 12,
                              height: 28,
                              textTransform: 'none',
                            }}
                            endIcon={<ArrowDownwardIcon sx={{ fontSize: 14 }} />}
                            onClick={(e): void => {
                              setOemSearchQuery('');
                              setOemMenuAnchor(e.currentTarget);
                            }}
                          >
                            OEMs
                            {oemFilterCount > 0 ? (
                              <>
                                {' '}
                                <Box
                                  component="span"
                                  sx={{ color: 'success.main', fontWeight: 500 }}
                                >
                                  {`(${oemFilterCount})`}
                                </Box>
                              </>
                            ) : null}
                          </Button>
                          <Menu
                            anchorEl={oemMenuAnchor}
                            open={Boolean(oemMenuAnchor)}
                            onClose={(): void => {
                              setOemMenuAnchor(null);
                              setOemSearchQuery('');
                            }}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{
                              sx: {
                                maxHeight: 380,
                                minWidth: 200,
                                width: 'max-content',
                                maxWidth: 500,
                                display: 'flex',
                                flexDirection: 'column',
                              },
                            }}
                            MenuListProps={{
                              sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                minHeight: 0,
                                p: 0,
                                overflow: 'hidden',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                flexShrink: 0,
                                p: 1,
                                borderBottom: 1,
                                borderColor: 'divider',
                              }}
                            >
                              <TextField
                                size="small"
                                placeholder="Search OEMs"
                                value={oemSearchQuery}
                                onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                                  setOemSearchQuery(e.target.value)
                                }
                                variant="outlined"
                                fullWidth
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                  ),
                                  endAdornment: oemSearchQuery ? (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="Clear OEM search"
                                        size="small"
                                        onClick={(): void => setOemSearchQuery('')}
                                      >
                                        <ClearIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ) : undefined,
                                }}
                              />
                            </Box>
                            <List dense sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                              {oemFilter
                                .filter(
                                  (oem): boolean =>
                                    !oemSearchQuery.trim() ||
                                    oem.label
                                      .toLowerCase()
                                      .includes(oemSearchQuery.trim().toLowerCase())
                                )
                                .map(
                                  (oem): JSX.Element => (
                                    <MenuItem
                                      key={oem.value}
                                      onClick={(): void => handleOemToggle(oem.value)}
                                    >
                                      <Checkbox checked={oem.checked} disableRipple size="small" />
                                      <ListItemText
                                        primary={oem.label}
                                        sx={{ '& span': { whiteSpace: 'nowrap' } }}
                                      />
                                    </MenuItem>
                                  )
                                )}
                              {oemSearchQuery.trim() &&
                                oemFilter.filter((oem): boolean =>
                                  oem.label
                                    .toLowerCase()
                                    .includes(oemSearchQuery.trim().toLowerCase())
                                ).length === 0 && (
                                  <Typography
                                    sx={{ fontSize: 12, color: 'text.secondary', px: 2, py: 1 }}
                                  >
                                    No OEMs match your search.
                                  </Typography>
                                )}
                            </List>
                          </Menu>
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{
                              minWidth: 100,
                              justifyContent: 'space-between',
                              fontSize: 12,
                              height: 28,
                              textTransform: 'none',
                            }}
                            endIcon={<ArrowDownwardIcon sx={{ fontSize: 14 }} />}
                            onClick={(e): void => {
                              setDealerGroupSearchQuery('');
                              setDealerGroupMenuAnchor(e.currentTarget);
                            }}
                          >
                            Dealer Groups
                            {dealerGroupFilterCount > 0 ? (
                              <>
                                {'  '}
                                <Box
                                  component="span"
                                  sx={{ color: 'success.main', fontWeight: 500 }}
                                >
                                  {`(${dealerGroupFilterCount})`}
                                </Box>
                              </>
                            ) : null}
                          </Button>
                          <Menu
                            anchorEl={dealerGroupMenuAnchor}
                            open={Boolean(dealerGroupMenuAnchor)}
                            onClose={(): void => {
                              setDealerGroupMenuAnchor(null);
                              setDealerGroupSearchQuery('');
                            }}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{
                              sx: {
                                maxHeight: 380,
                                minWidth: 200,
                                width: 'max-content',
                                maxWidth: 500,
                                display: 'flex',
                                flexDirection: 'column',
                              },
                            }}
                            MenuListProps={{
                              sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                minHeight: 0,
                                p: 0,
                                overflow: 'hidden',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                flexShrink: 0,
                                p: 1,
                                borderBottom: 1,
                                borderColor: 'divider',
                              }}
                            >
                              <TextField
                                size="small"
                                placeholder="Search dealer groups"
                                value={dealerGroupSearchQuery}
                                onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                                  setDealerGroupSearchQuery(e.target.value)
                                }
                                variant="outlined"
                                fullWidth
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                  ),
                                  endAdornment: dealerGroupSearchQuery ? (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="Clear dealer group search"
                                        size="small"
                                        onClick={(): void => setDealerGroupSearchQuery('')}
                                      >
                                        <ClearIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ) : undefined,
                                }}
                              />
                            </Box>
                            <List dense sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                              {dealerGroupFilterDisplayed
                                .filter(
                                  (dg): boolean =>
                                    !dealerGroupSearchQuery.trim() ||
                                    dg.label
                                      .toLowerCase()
                                      .includes(dealerGroupSearchQuery.trim().toLowerCase())
                                )
                                .map(
                                  (dg): JSX.Element => (
                                    <MenuItem
                                      key={dg.value}
                                      onClick={(): void => handleDealerGroupToggle(dg.value)}
                                    >
                                      <Checkbox checked={dg.checked} disableRipple size="small" />
                                      <ListItemText
                                        primary={dg.label}
                                        sx={{ '& span': { whiteSpace: 'nowrap' } }}
                                      />
                                    </MenuItem>
                                  )
                                )}
                              {dealerGroupSearchQuery.trim() &&
                                dealerGroupFilterDisplayed.filter((dg): boolean =>
                                  dg.label
                                    .toLowerCase()
                                    .includes(dealerGroupSearchQuery.trim().toLowerCase())
                                ).length === 0 && (
                                  <Typography
                                    sx={{ fontSize: 12, color: 'text.secondary', px: 2, py: 1 }}
                                  >
                                    No dealer groups match your search.
                                  </Typography>
                                )}
                            </List>
                          </Menu>
                          <Button
                            id="dealer-refine-button"
                            aria-haspopup="listbox"
                            aria-expanded={dealerRefineMenuOpen}
                            onClick={handleDealerRefineMenuOpen}
                            variant="outlined"
                            size="small"
                            sx={{
                              minWidth: 120,
                              justifyContent: 'space-between',
                              fontSize: 12,
                              height: 28,
                              textTransform: 'none',
                            }}
                            endIcon={<ArrowDownwardIcon sx={{ fontSize: 14 }} />}
                          >
                            Dealers
                            {dealerRefineCount > 0 ? (
                              <>
                                {' '}
                                <Box
                                  component="span"
                                  sx={{ color: 'success.main', fontWeight: 500 }}
                                >
                                  {`(${dealerRefineCount})`}
                                </Box>
                              </>
                            ) : null}
                          </Button>
                          <Menu
                            anchorEl={dealerRefineMenuAnchor}
                            open={Boolean(dealerRefineMenuAnchor)}
                            onClose={handleDealerRefineMenuClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{
                              sx: {
                                maxHeight: 380,
                                width: 280,
                                display: 'flex',
                                flexDirection: 'column',
                              },
                            }}
                            MenuListProps={{
                              sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                minHeight: 0,
                                p: 0,
                                overflow: 'hidden',
                              },
                            }}
                          >
                            <Box
                              sx={{ flexShrink: 0, p: 1, borderBottom: 1, borderColor: 'divider' }}
                            >
                              <TextField
                                size="small"
                                placeholder="Search dealers"
                                value={dealerRefineSearchQuery}
                                onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                                  setDealerRefineSearchQuery(e.target.value)
                                }
                                variant="outlined"
                                fullWidth
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                  ),
                                  endAdornment: dealerRefineSearchQuery ? (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="Clear dealer search"
                                        size="small"
                                        onClick={(): void => setDealerRefineSearchQuery('')}
                                      >
                                        <ClearIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ) : undefined,
                                }}
                              />
                            </Box>
                            <List dense sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                              <ListItemButton onClick={handlePendingToggleAll}>
                                <ListItemIcon>
                                  <Checkbox
                                    checked={isAllDealersInListSelected}
                                    edge="start"
                                    disableRipple
                                  />
                                </ListItemIcon>
                                <ListItemText primary="All" />
                              </ListItemButton>
                              {dealerRefineFilteredList.map(
                                (roof): JSX.Element => (
                                  <ListItemButton
                                    key={roof.id}
                                    onClick={(): void => handlePendingToggleDealer(roof.id)}
                                  >
                                    <ListItemIcon>
                                      <Checkbox
                                        checked={
                                          pendingDealerIds !== null &&
                                          pendingDealerIds.includes(roof.id)
                                        }
                                        edge="start"
                                        disableRipple
                                      />
                                    </ListItemIcon>
                                    <ListItemText primary={roof.name} />
                                  </ListItemButton>
                                )
                              )}
                              {dealerRefineSearchQuery.trim() &&
                                dealerRefineFilteredList.length === 0 && (
                                  <Typography
                                    sx={{ fontSize: 12, color: 'text.secondary', px: 2, py: 1 }}
                                  >
                                    No dealers match your search.
                                  </Typography>
                                )}
                            </List>
                            <Box
                              sx={{
                                flexShrink: 0,
                                p: 1.5,
                                borderTop: 1,
                                borderColor: 'divider',
                                backgroundColor: 'background.paper',
                                display: 'flex',
                                gap: 1,
                              }}
                            >
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={handleDealerRefineReset}
                                sx={{ flex: 1 }}
                              >
                                Reset
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={handleDealerRefineApply}
                                sx={{ flex: 1 }}
                              >
                                Apply
                              </Button>
                            </Box>
                          </Menu>
                          <Tooltip
                            title="Select dealers to refine the list"
                            arrow
                            placement="top"
                            slotProps={{
                              popper: {
                                modifiers: [{ name: 'flip', enabled: true }],
                                sx: { zIndex: 1300 },
                              },
                            }}
                          >
                            <span style={{ display: 'inline-flex', lineHeight: 0 }}>
                              <InfoOutlinedIcon
                                sx={{ fontSize: 18, color: 'action.active', cursor: 'help' }}
                                aria-label="Select dealers to refine the list"
                              />
                            </span>
                          </Tooltip>
                        </>
                      </Box>
                    </TableCell>
                    {!isSchedule && (
                      <>
                        <TableCell
                          sx={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 700 }}
                          onClick={handleSort('amount')}
                        >
                          Amount{renderSortIcon('amount')}
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 700 }}
                          onClick={handleSort('warrcloud')}
                        >
                          WarrCloud{renderSortIcon('warrcloud')}
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 700 }}
                          onClick={handleSort('dealerCount')}
                        >
                          Dealer{renderSortIcon('dealerCount')}
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 700 }}
                          onClick={handleSort('oem')}
                        >
                          OEM{renderSortIcon('oem')}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow
                    onClick={handleSelectDealer(null)}
                    sx={{
                      ...rowClickSx,
                      backgroundColor: (theme: Theme): string =>
                        !selectedDealerId ? alpha(theme.palette.primary.main, 0.2) : 'transparent',
                    }}
                  >
                    <TableCell sx={{ fontWeight: !selectedDealerId ? 700 : 400 }}>All</TableCell>
                    {!isSchedule && (
                      <>
                        <TableCell
                          sx={{ fontWeight: !selectedDealerId ? 700 : 400, textAlign: 'right' }}
                        >
                          {formatAmount(totals.amount)}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'right',
                            color: getCountColor(totals.warrcloud),
                            fontWeight: !selectedDealerId ? 700 : 600,
                          }}
                        >
                          {totals.warrcloud}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'right',
                            color: getCountColor(totals.dealer),
                            fontWeight: !selectedDealerId ? 700 : 600,
                          }}
                        >
                          {totals.dealer}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'right',
                            color: getCountColor(totals.oem),
                            fontWeight: !selectedDealerId ? 700 : 600,
                          }}
                        >
                          {totals.oem}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                  {sortedDealers.map(dealer => {
                    const needsScheduleUpload = shouldHighlightScheduleUpload(dealer);
                    const isSelected = dealer.roofTopId === selectedDealerId;
                    const getRowBg = (theme: Theme): string => {
                      if (needsScheduleUpload) return alpha(theme.palette.warning.main, 0.22);
                      if (isSelected) return alpha(theme.palette.primary.main, 0.2);
                      return 'transparent';
                    };
                    return (
                      <TableRow
                        key={dealer.roofTopId}
                        ref={isSelected ? selectedDealerRowRef : undefined}
                        onClick={handleSelectDealer(dealer.roofTopId)}
                        title={
                          needsScheduleUpload ? 'Please upload a Schedule' : dealer.roofTopName
                        }
                        sx={{
                          ...rowClickSx,
                          backgroundColor: getRowBg,
                          ...(isSelected && { scrollMarginTop: 36 }),
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: isSelected ? 700 : 400,
                            color: (theme: Theme): string =>
                              needsScheduleUpload ? theme.palette.warning.dark : 'inherit',
                          }}
                        >
                          {dealer.roofTopName}
                        </TableCell>
                        {!isSchedule && (
                          <>
                            <TableCell
                              sx={{ fontWeight: isSelected ? 700 : 400, textAlign: 'right' }}
                            >
                              {formatAmount(dealer.amount)}
                            </TableCell>
                            <TableCell
                              sx={{
                                textAlign: 'right',
                                color: getCountColor(dealer.assignedToWCCount),
                                fontWeight: isSelected ? 700 : 600,
                              }}
                            >
                              {dealer.assignedToWCCount}
                            </TableCell>
                            <TableCell
                              sx={{
                                textAlign: 'right',
                                color: getCountColor(dealer.assignedToDealerCount),
                                fontWeight: isSelected ? 700 : 600,
                              }}
                            >
                              {dealer.assignedToDealerCount}
                            </TableCell>
                            <TableCell
                              sx={{
                                textAlign: 'right',
                                color: getCountColor(dealer.assignedToOEMCount),
                                fontWeight: isSelected ? 700 : 600,
                              }}
                            >
                              {dealer.assignedToOEMCount}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </DealerTableWrapStyled>
          </>
        )}
      </InfoCard>
    </ReportCardWrapStyled>
  );
};

export default DealerCard;
